/**
 * ClientOnboarding — first-time wizard for a signed-in client with no deal.
 *
 * Dynamic, agent-aware (Phase 10 step 9):
 *   1. Name — "Welcome! What's your name?"
 *   2. Service — only the services the client's AGENT offers, decided server-side
 *      by the agent's phone linkage across brokerages (a dual-licensed RE+MLO
 *      agent unlocks "Buy + Get a Mortgage"). Shows who the client is working
 *      with (agent name + phone via link, or the brokerage line).
 *   3. Confirm — the services + brokerage(s)/license being set up.
 *
 * On submit, POST /document/self-onboard creates the client record + dossier AND
 * opens the Phase-10 deal. The agent context (from the invite link) is carried in
 * sessionStorage so it survives registration + SPA exploring — if it is ever
 * lost we tell the client to use their invite link again.
 */

import { useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  FileText,
  Home,
  Key,
  KeyRound,
  Landmark,
  Loader2,
  Phone,
  TriangleAlert,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { ApiClientError } from "@re-platform/api-client";

import { loadSession } from "../auth/session";

import { api } from "../../lib/api";
import {
  clearOnboardingAgent,
  loadOnboardingAgent,
  saveOnboardingAgent,
  type OnboardingAgent,
} from "./onboardingAgent";
import {
  COMMUNITY_PROPERTY_SERVICES,
  EMPTY_LISTING_ANSWERS,
  isListingService,
  LISTING_SERVICES,
  type ListingAnswers,
  ListingDetailsFields,
  listingProblems,
  listingRequestBody,
  type Service,
  withSuggestedTerm,
} from "./listingDetails";
import "./ClientOnboarding.css";

interface ServiceOption {
  id: Service;
  icon: typeof Home;
  label: string;
  description: string;
}

const SERVICES: readonly ServiceOption[] = [
  {
    id: "buy",
    icon: Home,
    label: "Buy a Home",
    description:
      "Purchase a property — I have my own financing or paying cash.",
  },
  {
    id: "buy_loan",
    icon: Landmark,
    label: "Buy + Get a Mortgage",
    description:
      "I need both a buyer's agent and mortgage financing in one place.",
  },
  {
    id: "rent",
    icon: Key,
    label: "Rent a Home",
    description: "Find a rental property and complete a tenant application.",
  },
  {
    id: "sell",
    icon: Building2,
    label: "Sell My Home",
    description:
      "List my property — I need a seller's agent and disclosure paperwork.",
  },
  {
    id: "lease",
    icon: KeyRound,
    label: "Lease Out My Property",
    description:
      "List my property for rent — I need a listing agent to find and screen tenants.",
  },
  {
    id: "mortgage",
    icon: FileText,
    label: "Get Pre-Approved",
    description:
      "Mortgage pre-approval or refinance — no property search needed yet.",
  },
];

interface OnboardingSide {
  brokerage_type: string;
  tenant_name: string;
  license_number: string | null;
}

interface OnboardingOptions {
  via_link: boolean;
  /** The agent id the SERVER resolved and validated. Prefer it over our stored
   *  ref when submitting — the stored one may be an agents(id) from a landing
   *  URL, or stale enough to no longer exist. */
  agent_user_id: string | null;
  agent_name: string | null;
  contact_phone: string | null;
  is_dual: boolean;
  offered_services: string[];
  sides: OnboardingSide[];
  /** The agent's rate-card term, used only to OFFER a listing end date. ¶4A is
   *  negotiated per property, so the client may change it and theirs is what is
   *  stored; null means neither the agent nor the brokerage has set one and we
   *  leave the end date blank rather than inventing a term. */
  listing_term_months: number | null;
}

type Step = "name" | "service" | "listing" | "household" | "confirm";

/** The only three terms ECOA / Reg B §1002.5(d)(1) allows to be asked on a
 *  credit application — "unmarried" is defined to cover single, divorced and
 *  widowed, so offering "single" separately is not permitted. It is also
 *  exactly what the spouse-signing rule needs: married means a spouse signs,
 *  separated means one still might (the marriage exists), unmarried means
 *  there is none. */
type MaritalStatus = "married" | "unmarried" | "separated";

type Status = "idle" | "loading" | { error: string };

const AGENT_REF_KEY = "rp.agent_ref";

/**
 * The wizard's in-progress answers, persisted across a session hiccup.
 *
 * The client session token is short-lived (15 min — SEC-4, see identity-svc
 * settings.py). Refresh rotation IS wired up now, so a session no longer dies
 * mid-form — but a draft still outlives a closed tab or a flat battery, and
 * losing a half-filled wizard is its own reason to keep this; without it they'd
 * also lose everything they'd typed. Saved on every change, restored on
 * mount, cleared only on a successful submit.
 */
const DRAFT_KEY = "rp.onboarding_draft";

interface OnboardingDraft {
  /** Whose draft this is.
   *
   *  🔴 The key is a single global localStorage entry and `clearDraft()` only
   *  ran on a SUCCESSFUL submit — never on logout or an account switch. So a new
   *  account signing in on the same browser resumed the PREVIOUS person's
   *  half-filled wizard: restored to their review step, carrying their service
   *  and their answers, and submitting them. Reported from real use at #20 as
   *  "it took me directly to step 5".
   *
   *  Stamped rather than namespaced into the key so an older draft (which has no
   *  userId) is discarded rather than silently adopted. */
  userId?: string;
  step: Step;
  firstName: string;
  lastName: string;
  selected: Service | null;
  phone: string;
  maritalStatus: MaritalStatus | "";
  addressStreet: string;
  addressCity: string;
  addressState: string;
  addressZip: string;
  jointApplication?: boolean | null;
  spouseFirst: string;
  spouseLast: string;
  spouseEmail: string;
  spousePhone: string;
  listing?: ListingAnswers;
}

function loadDraft(userId: string | null): OnboardingDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as OnboardingDraft;
    // Somebody else's half-finished wizard — or one saved before drafts were
    // stamped at all. Either way it is not this person's, and resuming it puts
    // them on a review step full of answers they never gave.
    if (!userId || draft.userId !== userId) {
      clearDraft();
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

function saveDraft(draft: OnboardingDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // best-effort — private mode / quota just means no recovery, not a crash
  }
}

function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}

/** The listing step only exists for a seller or a landlord, so the order — and
 *  therefore "Step 3 of 5" — depends on what they picked. A fixed order would
 *  either show a buyer a step they never reach or miscount everyone else. */
function stepOrder(service: Service | null): Step[] {
  return service && LISTING_SERVICES.has(service)
    ? ["name", "service", "listing", "household", "confirm"]
    : ["name", "service", "household", "confirm"];
}
const STEP_LABELS: Record<Step, string> = {
  name: "You",
  service: "Service",
  listing: "Property",
  household: "Details",
  confirm: "Review",
};

function Stepper({
  current,
  service,
}: {
  current: Step;
  service: Service | null;
}) {
  const order = stepOrder(service);
  const idx = order.indexOf(current);
  return (
    <>
      <div className="co-stepper">
        {order.map((st, i) => (
          <Fragment key={st}>
            <span
              className={
                "co-dot " +
                (i < idx ? "co-dot--done" : i === idx ? "co-dot--active" : "")
              }
            >
              {i < idx ? "✓" : i + 1}
            </span>
            {i < order.length - 1 && (
              <span className={"co-conn " + (i < idx ? "co-conn--done" : "")} />
            )}
          </Fragment>
        ))}
      </div>
      <div className="co-stepcap">
        Step {idx + 1} of {order.length} · {STEP_LABELS[current]}
      </div>
    </>
  );
}

export function ClientOnboarding() {
  const sessionUserId = loadSession()?.userId ?? null;
  const draft = loadDraft(sessionUserId);
  const [step, setStep] = useState<Step>(draft?.step ?? "name");
  const [firstName, setFirstName] = useState(draft?.firstName ?? "");
  const [lastName, setLastName] = useState(draft?.lastName ?? "");
  const [selected, setSelected] = useState<Service | null>(
    draft?.selected ?? null,
  );
  const [phone, setPhone] = useState(draft?.phone ?? "");
  const [maritalStatus, setMaritalStatus] = useState<MaritalStatus | "">(
    draft?.maritalStatus ?? "",
  );
  const [jointApplication, setJointApplication] = useState<boolean | null>(
    draft?.jointApplication ?? null,
  );
  const [addressStreet, setAddressStreet] = useState(
    draft?.addressStreet ?? "",
  );
  const [addressCity, setAddressCity] = useState(draft?.addressCity ?? "");
  const [addressState, setAddressState] = useState(draft?.addressState ?? "");
  const [addressZip, setAddressZip] = useState(draft?.addressZip ?? "");
  const [spouseFirst, setSpouseFirst] = useState(draft?.spouseFirst ?? "");
  const [spouseLast, setSpouseLast] = useState(draft?.spouseLast ?? "");
  const [spouseEmail, setSpouseEmail] = useState(draft?.spouseEmail ?? "");
  const [spousePhone, setSpousePhone] = useState(draft?.spousePhone ?? "");
  const [listing, setListing] = useState<ListingAnswers>({
    ...EMPTY_LISTING_ANSWERS,
    ...(draft?.listing ?? {}),
  });
  const [submitStatus, setSubmitStatus] = useState<Status>("idle");

  // Persist on every change — cheap (localStorage, small object) and means a
  // session that expires mid-form loses nothing once they sign back in.
  useEffect(() => {
    saveDraft({
      userId: sessionUserId ?? undefined,
      step,
      firstName,
      lastName,
      selected,
      phone,
      maritalStatus,
      addressStreet,
      addressCity,
      addressState,
      addressZip,
      jointApplication,
      spouseFirst,
      spouseLast,
      spouseEmail,
      spousePhone,
      listing,
    });
  }, [
    step,
    firstName,
    lastName,
    selected,
    phone,
    maritalStatus,
    addressStreet,
    addressCity,
    addressState,
    addressZip,
    spouseFirst,
    spouseLast,
    spouseEmail,
    spousePhone,
    listing,
  ]);

  const [agent, setAgent] = useState<OnboardingAgent | null>(null);
  const [options, setOptions] = useState<OnboardingOptions | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Capture a legacy ?ref=<agent_id> link, then resolve the agent context and
  // ask the server which services this agent can offer (dual detection).
  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      localStorage.setItem(AGENT_REF_KEY, ref);
      saveOnboardingAgent({ agentId: ref });
    }
    const ctx = loadOnboardingAgent();
    setAgent(ctx);

    api
      .request<OnboardingOptions>("/document/onboarding-options", {
        query: {
          agent_id: ctx?.agentId,
          agent_tenant_id: ctx?.agentTenantId,
        },
      })
      .then(setOptions)
      .catch((err: unknown) =>
        setOptionsError(
          err instanceof Error ? err.message : "Could not load your options",
        ),
      );
  }, []);

  function handleNameNext(e: React.FormEvent) {
    e.preventDefault();
    if (firstName.trim() && lastName.trim()) setStep("service");
  }

  async function handleSubmit() {
    if (!selected) return;
    setSubmitStatus("loading");
    try {
      const created = await api.request<{
        dossier_id: string;
        deal_id: string | null;
      }>("/document/self-onboard", {
        method: "POST",
        body: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          service: selected,
          ...(options?.agent_user_id
            ? { assigned_agent_id: options.agent_user_id }
            : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(addressStreet.trim()
            ? {
                address_street: addressStreet.trim(),
                address_city: addressCity.trim(),
                address_state: addressState.trim().toUpperCase(),
                address_zip: addressZip.trim(),
              }
            : {}),
          ...listingRequestBody(selected, listing),
          ...(maritalStatus ? { marital_status: maritalStatus } : {}),
          ...(jointPossible ? { joint_application: jointApplication } : {}),
          ...((jointPossible && jointApplication) ||
          (spouseNeeded && maritalStatus === "married")
            ? {
                co_client_first_name: spouseFirst.trim(),
                co_client_last_name: spouseLast.trim(),
                co_client_email: spouseEmail.trim(),
                ...(spousePhone.trim()
                  ? { co_client_phone: spousePhone.trim() }
                  : {}),
              }
            : {}),
        },
      });
      clearOnboardingAgent();
      clearDraft();
      await queryClient.invalidateQueries({ queryKey: ["my-dossiers"] });
      // Straight into the application they just started, not the home page.
      // The response has carried `dossier_id` all along and it was discarded:
      // a FIRST-time client lands on a home page showing their only dossier, so
      // it looked right, but an EXISTING client gets a list — and the thing they
      // just created is one row among several, or below the fold. It read as
      // though choosing a service had done nothing at all.
      navigate(`/dossier/${created.dossier_id}`);
    } catch (err) {
      // Session expired mid-form (15 min TTL) — the draft is already saved
      // (every change persists it), so send them to log back in rather than
      // surfacing the raw "Missing bearer token" backend text.
      if (err instanceof ApiClientError && err.status === 401) {
        void navigate("/login");
        return;
      }
      setSubmitStatus({
        error:
          err instanceof ApiClientError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Something went wrong",
      });
    }
  }

  const isLoading = submitStatus === "loading";

  // Texas community property: buying or selling while married means the spouse signs
  // the representation agreement too, so we must know who they are and where to send
  // their signing link. Renting conveys no interest — we never ask about a spouse.
  const spouseNeeded = Boolean(
    selected && COMMUNITY_PROPERTY_SERVICES.has(selected),
  );
  // A JOINT BORROWER is not a community-property spouse. Two people can apply
  // for one loan whatever their marital status — an unmarried partner, a parent
  // co-signing, siblings — and it changes the DTI arithmetic and who signs.
  // Standalone mortgage only: on a purchase, the spouse question already covers
  // who signs, and asking both would be asking the same person twice.
  const jointPossible = selected === "mortgage";
  // A seller or landlord is putting a PROPERTY on the market, so ¶2A, ¶3 and ¶4A
  // of the listing agreement need answering — none of which anything else in the
  // system holds.
  const isListing = isListingService(selected);
  const listingIssues = listingProblems(listing);
  const jointComplete =
    !jointPossible ||
    jointApplication !== true ||
    (spouseFirst.trim() !== "" &&
      spouseLast.trim() !== "" &&
      spouseEmail.includes("@"));
  const spouseComplete =
    !spouseNeeded ||
    maritalStatus !== "married" ||
    (spouseFirst.trim() !== "" &&
      spouseLast.trim() !== "" &&
      spouseEmail.includes("@"));
  // The PHONE is asked of everyone, including renters: their showing confirmations are
  // sent by SMS, and nothing else in the platform has ever collected it.
  const detailsComplete =
    phone.trim() !== "" &&
    // ¶1 of the representation agreement prints the client's address. Nothing
    // collected it, so every BRA went out with that line blank.
    addressStreet.trim() !== "" &&
    addressCity.trim() !== "" &&
    addressState.trim() !== "" &&
    addressZip.trim() !== "" &&
    (!spouseNeeded || maritalStatus !== "") &&
    (!jointPossible || jointApplication !== null) &&
    spouseComplete &&
    jointComplete;

  // Which tiles to show: only the agent's offered services (server-decided).
  const offered = options?.offered_services ?? [];
  const tiles = SERVICES.filter((s) => offered.includes(s.id));
  const agentName = options?.agent_name ?? agent?.agentName ?? null;
  const contactPhone = options?.contact_phone ?? agent?.agentPhone ?? null;
  // We genuinely don't know the client's agent — prompt them to use their link.
  const agentUnknown =
    options !== null && !options.via_link && !agentName && !agent?.agentId;
  const selectedOption = SERVICES.find((s) => s.id === selected) ?? null;

  return (
    <div className="co-page">
      {step === "name" && (
        <section className="co-card">
          <Stepper current={step} service={selected} />
          <div className="co-welcome-badge">Welcome</div>
          <h1 className="co-title">Let's get you started</h1>
          <p className="co-sub">
            Tell us your name so your agent knows who they're working with.
          </p>
          <form className="co-form" onSubmit={handleNameNext}>
            <div className="co-fields">
              <label className="co-field">
                <span>First name</span>
                <input
                  type="text"
                  required
                  autoFocus
                  autoComplete="given-name"
                  placeholder="Maria"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </label>
              <label className="co-field">
                <span>Last name</span>
                <input
                  type="text"
                  required
                  autoComplete="family-name"
                  placeholder="Delgado"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </label>
            </div>
            <button
              type="submit"
              className="co-btn co-btn--primary"
              disabled={!firstName.trim() || !lastName.trim()}
            >
              Continue →
            </button>
          </form>
        </section>
      )}

      {step === "service" && (
        <section className="co-card">
          <Stepper current={step} service={selected} />
          <button
            type="button"
            className="co-back"
            onClick={() => setStep("name")}
          >
            ← Back
          </button>
          <h1 className="co-title">
            Hi {firstName}! What brings you here today?
          </h1>
          <p className="co-sub">
            We'll set up the right document checklist for you automatically.
          </p>

          {/* Who you're working with — agent (via link) or brokerage line. */}
          {agentName && (
            <div className="co-agent">
              You're working with <strong>{agentName}</strong>
              {contactPhone && (
                <span className="co-agent__phone">
                  <Phone size={14} aria-hidden /> {contactPhone}
                </span>
              )}
            </div>
          )}
          {!agentName && contactPhone && (
            <div className="co-agent">
              Questions? Call <strong>{contactPhone}</strong>
            </div>
          )}
          {agentUnknown && (
            <div className="co-notice" role="status">
              <TriangleAlert size={16} aria-hidden />
              <span>
                We couldn't tell which agent you're working with. If you have an
                invite link, please open it again — or ask your agent to resend
                it. You can still continue below.
              </span>
            </div>
          )}

          {optionsError && (
            <div className="co-error" role="alert">
              {optionsError}
            </div>
          )}

          {options === null && optionsError === null ? (
            <div className="co-loading">
              <Loader2 className="co-spin" size={20} aria-hidden />
              Loading your options…
            </div>
          ) : (
            <div className="co-services">
              {tiles.map((svc) => {
                const Icon = svc.icon;
                const isSelected = selected === svc.id;
                return (
                  <button
                    key={svc.id}
                    type="button"
                    className={`co-svc ${isSelected ? "co-svc--selected" : ""}`}
                    onClick={() => setSelected(svc.id)}
                  >
                    <div className="co-svc__icon">
                      <Icon size={22} strokeWidth={2} />
                    </div>
                    <div className="co-svc__body">
                      <div className="co-svc__label">{svc.label}</div>
                      <div className="co-svc__desc">{svc.description}</div>
                    </div>
                    {isSelected && <div className="co-svc__check">✓</div>}
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button"
            className="co-btn co-btn--primary"
            disabled={!selected}
            onClick={() => {
              if (isListing) {
                // Offer the rate card's term as a starting point rather than two
                // empty pickers. ¶4A is negotiated per property, so the client
                // may change both and theirs are what get stored — but a seller
                // shown two blanks has no idea what is expected.
                setListing((prev) =>
                  withSuggestedTerm(prev, options?.listing_term_months),
                );
              }
              setStep(isListing ? "listing" : "household");
            }}
          >
            Next →
          </button>
        </section>
      )}

      {step === "listing" && (
        <section className="co-card">
          <Stepper current={step} service={selected} />
          <button
            type="button"
            className="co-back"
            onClick={() => setStep("service")}
          >
            ← Back
          </button>
          <h1 className="co-title">
            {selected === "lease"
              ? "The property you're renting out"
              : "The property you're selling"}
          </h1>
          {/* Every field here is optional and the agent completes the agreement
              before it is sent, so nothing below blocks the application. Saying
              so matters: a seller who does not yet know their listing term
              should not abandon the form over it. */}
          <p className="co-sub">
            This goes on your listing agreement. If you're not sure of something
            yet, leave it — your agent will finish it with you before you sign.
          </p>
          <ListingDetailsFields
            service={selected}
            answers={listing}
            onChange={setListing}
          />

          <button
            type="button"
            className="co-btn co-btn--primary"
            disabled={listingIssues.any}
            onClick={() => setStep("household")}
          >
            Next →
          </button>
        </section>
      )}

      {step === "household" && (
        <section className="co-card">
          <Stepper current={step} service={selected} />
          <button
            type="button"
            className="co-back"
            onClick={() => setStep(isListing ? "listing" : "service")}
          >
            ← Back
          </button>
          <h1 className="co-title">A couple of details</h1>
          <p className="co-sub">
            {spouseNeeded
              ? "We'll text you about your showings — and Texas is a community property state, so if you're married your spouse has an interest in the property too, and signs the agreement with you."
              : "We'll text you to confirm your showings."}
          </p>

          <label className="co-field">
            <span>Your mobile number</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(512) 555-0142"
              autoComplete="tel"
            />
          </label>

          {/* ¶1 of the representation agreement prints this. Nothing collected
              it before, so every BRA reached a real buyer with the address line
              blank — and the client had nowhere to write it, because a flat PDF
              has no boxes we did not place. */}
          <label className="co-field">
            <span>Your home address</span>
            <input
              value={addressStreet}
              onChange={(e) => setAddressStreet(e.target.value)}
              placeholder="4218 Travis Heights Blvd"
              autoComplete="street-address"
            />
          </label>
          <div className="co-field-row">
            <label className="co-field">
              <span>City</span>
              <input
                value={addressCity}
                onChange={(e) => setAddressCity(e.target.value)}
                placeholder="Austin"
                autoComplete="address-level2"
              />
            </label>
            <label className="co-field">
              <span>State</span>
              <input
                value={addressState}
                onChange={(e) => setAddressState(e.target.value)}
                placeholder="TX"
                maxLength={2}
                autoComplete="address-level1"
              />
            </label>
            <label className="co-field">
              <span>ZIP</span>
              <input
                value={addressZip}
                onChange={(e) => setAddressZip(e.target.value)}
                placeholder="78704"
                autoComplete="postal-code"
              />
            </label>
          </div>

          {/* Only where community property bites: buying or selling. A lease conveys
              no property interest, so we never ask a renter about their marriage. */}
          {spouseNeeded && (
            <label className="co-field">
              <span>Marital status</span>
              <select
                value={maritalStatus}
                onChange={(e) =>
                  setMaritalStatus(e.target.value as MaritalStatus | "")
                }
              >
                <option value="">Select…</option>
                <option value="married">Married</option>
                <option value="unmarried">Unmarried</option>
                <option value="separated">Separated</option>
              </select>
            </label>
          )}

          {/* Standalone mortgage: are two people on this loan? Deliberately its
              own question — being married does not make someone a borrower, and
              a co-borrower need not be a spouse. */}
          {jointPossible && (
            <label className="co-field">
              <span>Is anyone applying for this loan with you?</span>
              <select
                value={
                  jointApplication === null
                    ? ""
                    : jointApplication
                      ? "yes"
                      : "no"
                }
                onChange={(e) =>
                  setJointApplication(
                    e.target.value === "" ? null : e.target.value === "yes",
                  )
                }
              >
                <option value="">Select…</option>
                <option value="no">No, just me</option>
                <option value="yes">Yes, we're applying together</option>
              </select>
            </label>
          )}

          {jointPossible && jointApplication === true && (
            <>
              <p className="co-sub" style={{ marginTop: 16 }}>
                Both of you are liable for the loan, so we'll count both incomes
                — and they'll get their own link to sign. They don't need an
                account.
              </p>
              <label className="co-field">
                <span>Their first name</span>
                <input
                  value={spouseFirst}
                  onChange={(e) => setSpouseFirst(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="co-field">
                <span>Their last name</span>
                <input
                  value={spouseLast}
                  onChange={(e) => setSpouseLast(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="co-field">
                <span>Their email</span>
                <input
                  type="email"
                  value={spouseEmail}
                  onChange={(e) => setSpouseEmail(e.target.value)}
                  placeholder="Where we send their signing link"
                  autoComplete="off"
                />
              </label>
              <label className="co-field">
                <span>Their phone (optional)</span>
                <input
                  type="tel"
                  value={spousePhone}
                  onChange={(e) => setSpousePhone(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </>
          )}

          {spouseNeeded && maritalStatus === "married" && (
            <>
              <p className="co-sub" style={{ marginTop: 16 }}>
                Texas is a community property state, so your spouse signs the
                agreement too. <strong>Their email address is required</strong>{" "}
                — it is where DocuSign sends their own signing link, and without
                a correct one the agreement can never be completed. They don't
                need an account. You'll also be asked for a copy of their
                driver's licence.
              </p>
              <label className="co-field">
                <span>Spouse's first name</span>
                <input
                  value={spouseFirst}
                  onChange={(e) => setSpouseFirst(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="co-field">
                <span>Spouse's last name</span>
                <input
                  value={spouseLast}
                  onChange={(e) => setSpouseLast(e.target.value)}
                  autoComplete="off"
                />
              </label>
              <label className="co-field">
                <span>Spouse's email (required)</span>
                <input
                  type="email"
                  required
                  value={spouseEmail}
                  onChange={(e) => setSpouseEmail(e.target.value)}
                  placeholder="Where their signing link is sent"
                  autoComplete="off"
                />
              </label>
              <label className="co-field">
                <span>Spouse's phone (optional)</span>
                <input
                  type="tel"
                  value={spousePhone}
                  onChange={(e) => setSpousePhone(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </>
          )}

          <button
            type="button"
            className="co-btn co-btn--primary"
            disabled={!detailsComplete}
            onClick={() => setStep("confirm")}
          >
            Review →
          </button>
        </section>
      )}

      {step === "confirm" && selectedOption && (
        <section className="co-card">
          <Stepper current={step} service={selected} />
          <button
            type="button"
            className="co-back"
            onClick={() => setStep("household")}
          >
            ← Back
          </button>
          <h1 className="co-title">Confirm your setup</h1>
          <p className="co-sub">
            Here's what we'll set up for you. You can upload everything in one
            place.
          </p>

          <div className="co-confirm">
            <div className="co-confirm__row">
              <span className="co-confirm__label">Service</span>
              <strong>{selectedOption.label}</strong>
            </div>
            {agentName && (
              <div className="co-confirm__row">
                <span className="co-confirm__label">Your agent</span>
                <strong>{agentName}</strong>
              </div>
            )}
            {/* A dual deal touches both brokerages; license numbers already
                show on the agent's own landing page, so this summary keeps
                just the brokerage name per side. */}
            {(options?.sides ?? []).map((side) => (
              <div className="co-confirm__row" key={side.brokerage_type}>
                <span className="co-confirm__label">
                  {side.brokerage_type === "MORTGAGE"
                    ? "Home Loan"
                    : "Real Estate"}
                </span>
                <strong>{side.tenant_name}</strong>
              </div>
            ))}
          </div>

          {typeof submitStatus === "object" && (
            <div className="co-error" role="alert">
              {submitStatus.error}
            </div>
          )}

          {/* Re-checked HERE, not only on the listing step's Next button.
              A per-step guard is not a guard when the step can be SKIPPED, and
              a restored draft lands straight on this one — so an inverted date
              range reached the server and came back a bare 422 with nothing on
              screen to explain it. */}
          {isListing && listingIssues.any && (
            <div className="co-error" role="status">
              {listingIssues.termInverted
                ? "The listing cannot end before it begins."
                : "The longest lease term must be at least the shortest."}{" "}
              <button
                type="button"
                className="co-linkbtn"
                onClick={() => setStep("listing")}
              >
                Go back and fix it
              </button>
            </div>
          )}
          <button
            type="button"
            className="co-btn co-btn--primary"
            disabled={isLoading || (isListing && listingIssues.any)}
            onClick={() => void handleSubmit()}
          >
            {isLoading ? (
              <>
                <Loader2 className="co-spin" size={18} aria-hidden />
                Setting up your account…
              </>
            ) : (
              "Confirm & get started →"
            )}
          </button>
        </section>
      )}
    </div>
  );
}
